import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  PasswordInput,
  Alert,
  Loader,
  Center,
  Box,
  Group,
  Badge,
  useMantineColorScheme,
} from "@mantine/core";
import { useAppForm } from "@/hooks/useAppForm";
import {
  IconAlertCircle,
  IconCheck,
  IconMail,
  IconBuilding,
  IconUser,
} from "@tabler/icons-react";
import {
  PASSWORD_POLICY_DESCRIPTION,
  validatePasswordPolicy,
} from "@livingsyncs/types";
import api from "@/lib/apiClient";

interface InvitationDetails {
  first_name: string;
  last_name: string;
  email: string;
  position: string;
  organization_name: string;
  expires_at: string;
  sent_at: string;
}

export function AcceptInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { colorScheme } = useMantineColorScheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useAppForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value) => validatePasswordPolicy(value),
      confirmPassword: (value, values) => {
        if (!value) return "Please confirm your password";
        if (value !== values.password) return "Passwords do not match";
        return null;
      },
    },
  });

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError(
          "No invitation token provided. Please check your invitation link.",
        );
        setVerifying(false);
        return;
      }

      try {
        const response = await api.get(`/invitations/${token}`);
        setInvitation(response.data.data.invitation);
        setVerifying(false);
      } catch (err) {
        const error = err as { response?: { data?: { error?: string } } };
        const errorMessage =
          error.response?.data?.error ||
          "Invalid or expired invitation. Please contact your administrator.";
        setError(errorMessage);
        setVerifying(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleSubmit = async (values: typeof form.values) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      await api.post("/invitations/accept", {
        token,
        password: values.password,
      });

      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login", {
          state: {
            message:
              "Account created successfully! Please log in with your new password.",
          },
        });
      }, 2000);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      const errorMessage =
        error.response?.data?.error ||
        "Failed to accept invitation. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container size={420} my={100}>
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              Verifying your invitation...
            </Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size={420} my={100}>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Alert
            icon={<IconAlertCircle size={20} />}
            title="Error"
            color="red"
            mb="lg"
          >
            {error}
          </Alert>
          <Button fullWidth onClick={() => navigate("/login")}>
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container size={420} my={100}>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack align="center" gap="lg">
            <Box
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheck size={32} color="white" />
            </Box>
            <div style={{ textAlign: "center" }}>
              <Title order={1} fz="h2">Welcome to LivingSyncs!</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Your account has been created successfully
              </Text>
            </div>
            <Alert
              icon={<IconCheck size={20} />}
              color="green"
              style={{ width: "100%" }}
            >
              Redirecting you to login...
            </Alert>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <Container size={520} my={100}>
      <Paper withBorder shadow="md" p={30} radius="md">
        <Stack gap="lg">
          <div>
            <Title order={2}>You're Invited!</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Complete your account setup to join {invitation.organization_name}
            </Text>
          </div>

          {/* Invitation Details */}
          <Paper
            withBorder
            p="md"
            radius="md"
            bg={colorScheme === "dark" ? "dark.6" : "gray.0"}
          >
            <Stack gap="sm">
              <Group gap="xs">
                <IconUser size={16} color="var(--mantine-color-green-6)" />
                <Text
                  size="sm"
                  fw={500}
                  c={colorScheme === "dark" ? "gray.1" : "dark.7"}
                >
                  {invitation.first_name} {invitation.last_name}
                </Text>
              </Group>
              <Group gap="xs">
                <IconMail size={16} color="var(--mantine-color-green-6)" />
                <Text
                  size="sm"
                  c={colorScheme === "dark" ? "gray.3" : "dark.6"}
                >
                  {invitation.email}
                </Text>
              </Group>
              <Group gap="xs">
                <IconBuilding size={16} color="var(--mantine-color-green-6)" />
                <Text
                  size="sm"
                  c={colorScheme === "dark" ? "gray.3" : "dark.6"}
                >
                  {invitation.organization_name}
                </Text>
              </Group>
              <Group gap="xs">
                <Badge color="green" variant="light">
                  {invitation.position}
                </Badge>
              </Group>
            </Stack>
          </Paper>

          {error && (
            <Alert
              icon={<IconAlertCircle size={20} />}
              title="Error"
              color="red"
            >
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <PasswordInput
                label="Password"
                placeholder="Create a secure password"
                required
                description={PASSWORD_POLICY_DESCRIPTION}
                {...form.getInputProps("password")}
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="Re-enter your password"
                required
                {...form.getInputProps("confirmPassword")}
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                size="md"
                style={{
                  background: loading
                    ? undefined
                    : "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                }}
              >
                Accept Invitation & Create Account
              </Button>
            </Stack>
          </form>

          <Text size="xs" c="dimmed" ta="center">
            After creating your account, you'll be redirected to log in
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
}
